import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import { useStaticQuery, graphql } from "gatsby";
import MDXRenderer from "gatsby-plugin-mdx/mdx-renderer";
import StickyBox from "react-sticky-box";
import { debounce } from "lodash";
import Img from "gatsby-image";

import Section from "../../components/Section/Section";
import Author from "../../components/Author/Author";
import BlogCategories from "../../components/BlogCategories/BlogCategories";
import SEO from "../../components/seo";
import TOC from "../../components/TOC/TOC";
import TimeDetail from "../../components/TimeDetail/TimeDetail";
import ShadowTitle from "../../components/ShadowTitle/ShadowTitle";
import DetailList from "../../components/DetailList/DetailList";
import IconDetail from "../../components/IconDetail/IconDetail";
import SidebarComponent from "../../components/SidebarComponent/SidebarComponent";
import BackdropImage from "../../components/BackdropImage/BackdropImage";

import { ReactComponent as Time } from "../../svg/time.svg";
import { ReactComponent as Twitter } from "../../svg/social-logos/twitter.svg";
import { ReactComponent as LinkedIn } from "../../svg/social-logos/linkedin.svg";

import "katex/dist/katex.min.css";
import "./Post.scss";
import DraftDetail from "../../components/DraftDetail/DraftDetail";

const Post = ({
  post,
  showTOC = true,
  sidebarComponents = [],
  showComments = true,
}) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          disqusShortname
          siteUrl
        }
      }
    }
  `);

  const publicUrl = site.siteMetadata.siteUrl + post.fields.path;

  const [currentSection, setCurrentSection] = useState(null);
  const postBody = useRef(null);

  // Comments
  const commentsWrapper = useRef();
  useEffect(() => {
    const wrapper = commentsWrapper.current;
    if (wrapper) {
      const script = document.createElement("script");
      script.src = "https://utteranc.es/client.js";
      script.async = true;
      script.crossOrigin = "anonymous";
      script.setAttribute("repo", "vihanb/bloggoat");
      script.setAttribute("issue-term", post.frontmatter.title);
      script.setAttribute("label", "💬 comment");
      script.setAttribute("theme", "github-dark");

      wrapper.appendChild(script);

      return () => {
        while (wrapper.firstChild) {
          wrapper.removeChild(wrapper.lastChild);
        }
      };
    }
  }, [post.frontmatter.title]);

  // Scroll logic
  useLayoutEffect(() => {
    const handleScroll = debounce((e) => {
      if (!postBody.current) {
        setCurrentSection(null);
        return;
      }

      const currentScroll = window.scrollY;
      const headings = postBody.current.querySelectorAll(
        "h1, h2, h3, h4, h5, h6"
      );

      let i = 0;
      for (; i < headings.length; i++) {
        const isVisible = currentScroll > headings[i].offsetTop;
        if (!isVisible) {
          break;
        }
      }

      const currentHeading = headings[i - 1];
      const currentHeadingId = currentHeading ? currentHeading.id : null;

      setCurrentSection(currentHeadingId);
    }, 10);

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <Section className="blog">
      <SEO
        title={post.frontmatter.title}
        description={post.frontmatter.description}
        canonicalLink={publicUrl}
        keywords={post.frontmatter.categories || []}
        meta={[
          {
            name: "twitter:label1",
            content: "Reading time",
          },
          {
            name: "twitter:data1",
            content: `${post.timeToRead} min read`,
          },
        ]}
        image={post.frontmatter.featuredImage?.seoImage?.fixed}
      />

      {post.frontmatter.featuredImage && (
        <BackdropImage>
          <Img
            fluid={post.frontmatter.featuredImage.childImageSharp.fluid}
            alt=""
          />
        </BackdropImage>
      )}

      <DetailList emphasized>
        {post.frontmatter.draft && <DraftDetail />}
        <TimeDetail time={post.frontmatter.date} />
        <IconDetail icon={<Time />}>{post.timeToRead} min read</IconDetail>
      </DetailList>

      <header className="blog__header">
        <ShadowTitle>{post.frontmatter.title}</ShadowTitle>
      </header>

      <Author />
      {post.frontmatter.categories && (
        <BlogCategories categories={post.frontmatter.categories} />
      )}

      <section className="blog__body">
        <article className="blog__body__intro">
          {post.frontmatter.description && (
            <p className="blog__intro">{post.frontmatter.description}</p>
          )}

          <article className="body" ref={postBody}>
            <MDXRenderer>{post.body}</MDXRenderer>
          </article>

          <section className="blog__comments">
            <div ref={commentsWrapper} />
          </section>
        </article>

        <div className="blog__body__spacer"></div>

        <aside className="blog__body__aside">
          <StickyBox offsetTop={64} offsetBottom={64}>
            {showTOC && post.tableOfContents.items && (
              <SidebarComponent title="Table of Contents" noIndent>
                <TOC
                  tableOfContents={post.tableOfContents}
                  highlight={currentSection}
                />
              </SidebarComponent>
            )}
            {sidebarComponents.map((component, index) => (
              <React.Fragment key={index}>{component}</React.Fragment>
            ))}
          </StickyBox>
        </aside>
      </section>
    </Section>
  );
};

export default Post;
